import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Bike, ChevronLeft, Clock, CreditCard, MessageCircle, Minus, Plus, Search, ShieldCheck, ShoppingBag, Sparkles, Star, Trash2, UserRound } from 'lucide-react';
import { useStore } from '../context/StoreContext';
import { Button } from '../components/Button';
import { ProductCustomizationModal } from '../components/ProductCustomizationModal';
import { Company, DeliveryCustomer, DeliveryFulfillmentType, DeliveryPaymentMethod, DeliverySettings, Product, ProductModifierSelection } from '../types';
import { formatCurrency, generateOrderId } from '../services/dataService';
import { api } from '../services/api';

import { useRef } from 'react';
import { getPendingRequest } from '../services/pendingRequests';

type CartItem = {
  id: string;
  product: Product;
  quantity: number;
  notes: string;
  modifierSelections?: ProductModifierSelection[];
};

const CUSTOMER_STORAGE_KEY = 'ft_delivery_customer_v1';
const DEFAULT_DELIVERY_SETTINGS: DeliverySettings = {
  isOpen: true, allowPickup: true, minimumOrder: 0, estimatedMinutes: 45, defaultDeliveryFee: 0, zones: []
};

const defaultCustomer: DeliveryCustomer = {
  name: '',
  phone: '',
  address: '',
  number: '',
  neighborhood: '',
  complement: '',
  reference: '',
  wantsWhatsappUpdates: true,
};

export const DeliveryMenu = () => {
  const navigate = useNavigate();
  const { companyId } = useParams();
  const { currentCompany, products, createDeliveryOrder } = useStore();
  const [publicCompany, setPublicCompany] = useState<Company | null>(null);
  const [publicProducts, setPublicProducts] = useState<Product[]>([]);
  const [isLoadingPublicMenu, setIsLoadingPublicMenu] = useState(false);
  const [cart, setCart] = useState<CartItem[]>([]);
  const [customizingProduct, setCustomizingProduct] = useState<Product | null>(null);
  const sending = useRef(false);
  const [isSending, setIsSending] = useState(false);
  useEffect(() => {
    const recovered = (event: Event) => {
      const { packet, result, recovered } = (event as CustomEvent).detail;
      if (!recovered || !packet.endpoint.endsWith('delivery-orders') || packet.body.companyId !== (companyId || currentCompany?.id)) return;
      setCart([]);
      navigate(`/delivery/track/${result.id}?token=${encodeURIComponent(result.publicToken || '')}`);
    };
    window.addEventListener('ft-request-confirmed', recovered);
    return () => window.removeEventListener('ft-request-confirmed', recovered);
  }, [companyId, currentCompany?.id, navigate]);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<DeliveryPaymentMethod>('Pix');
  const [fulfillmentType, setFulfillmentType] = useState<DeliveryFulfillmentType>('DELIVERY');
  const [changeFor, setChangeFor] = useState('');
  const [orderNotes, setOrderNotes] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('Todos');
  const [customer, setCustomer] = useState<DeliveryCustomer>(() => {
    try {
      return { ...defaultCustomer, ...JSON.parse(localStorage.getItem(CUSTOMER_STORAGE_KEY) || '{}') };
    } catch {
      return defaultCustomer;
    }
  });

  useEffect(() => {
    if (!companyId) return;

    let isMounted = true;
    setIsLoadingPublicMenu(true);

    Promise.all([
      api.publicMenu.getCompany(companyId),
      api.publicMenu.getProducts(companyId),
    ]).then(([company, companyProducts]) => {
      if (!isMounted) return;
      setPublicCompany(company || null);
      setPublicProducts(companyProducts || []);
    }).finally(() => {
      if (isMounted) setIsLoadingPublicMenu(false);
    });

    return () => { isMounted = false; };
  }, [companyId]);

  const menuCompany = publicCompany || currentCompany;
  const menuProducts = companyId ? publicProducts : products;
  const isDeliveryDisabled = menuCompany?.useDelivery !== true;
  const deliverySettings = menuCompany?.deliverySettings || DEFAULT_DELIVERY_SETTINGS;
  const matchingZone = deliverySettings.zones.find(zone => zone.name.trim().toLocaleLowerCase('pt-BR') === customer.neighborhood.trim().toLocaleLowerCase('pt-BR'));
  const regionUnavailable = fulfillmentType === 'DELIVERY' && deliverySettings.zones.length > 0 && Boolean(customer.neighborhood.trim()) && !matchingZone;

  const deliveryProducts = useMemo(() => {
    return menuProducts.filter(product => (
      product.availableForDelivery === true &&
      product.internalUseOnly !== true &&
      product.active !== false &&
      product.allowSale !== false
    ));
  }, [menuProducts]);

  const deliveryCategories = useMemo(() => {
    return Array.from(new Set(deliveryProducts.map(product => product.category))).sort();
  }, [deliveryProducts]);

  const featuredProducts = useMemo(() => deliveryProducts.slice(0, 6), [deliveryProducts]);

  const filteredProducts = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return deliveryProducts.filter(product => {
      const matchesCategory = activeCategory === 'Todos' || product.category === activeCategory;
      const matchesSearch = !normalizedSearch
        || product.name.toLowerCase().includes(normalizedSearch)
        || (product.description || '').toLowerCase().includes(normalizedSearch)
        || product.category.toLowerCase().includes(normalizedSearch);

      return matchesCategory && matchesSearch;
    });
  }, [activeCategory, deliveryProducts, searchTerm]);

  const productsByCategory = useMemo(() => {
    const visibleCategories = activeCategory === 'Todos'
      ? deliveryCategories
      : deliveryCategories.filter(category => category === activeCategory);

    return visibleCategories.map(category => ({
      category,
      items: filteredProducts.filter(product => product.category === category),
    })).filter(group => group.items.length > 0);
  }, [activeCategory, deliveryCategories, filteredProducts]);

  const subtotal = cart.reduce((sum, item) => sum + item.product.price * item.quantity, 0);
  const deliveryFee = fulfillmentType === 'PICKUP' ? 0 : (matchingZone?.fee ?? deliverySettings.defaultDeliveryFee);
  const total = subtotal + deliveryFee;

  const addToCart = (product: Product) => {
    if (sending.current || (menuCompany && getPendingRequest(menuCompany.id))) return;
    if ((product.modifierGroups || []).length > 0) {
      setCustomizingProduct(product);
      return;
    }
    setCart(prev => {
      const existing = prev.find(item => item.product.id === product.id && !item.notes);
      if (existing) {
        return prev.map(item => item.id === existing.id ? { ...item, quantity: item.quantity + 1 } : item);
      }

      return [...prev, { id: generateOrderId(), product, quantity: 1, notes: '' }];
    });
  };

  const confirmCustomization = (price: number, notes: string, modifierSelections: ProductModifierSelection[]) => {
    if (!customizingProduct) return;
    setCart(previous => [...previous, { id: generateOrderId(), product: { ...customizingProduct, price }, quantity: 1, notes, modifierSelections }]);
    setCustomizingProduct(null);
  };

  const changeQuantity = (itemId: string, change: number) => {
    if (sending.current || (menuCompany && getPendingRequest(menuCompany.id))) return;
    setCart(prev => prev
      .map(item => item.id === itemId ? { ...item, quantity: Math.max(0, item.quantity + change) } : item)
      .filter(item => item.quantity > 0)
    );
  };

  const removeItem = (itemId: string) => {
    if (sending.current || (menuCompany && getPendingRequest(menuCompany.id))) return;
    setCart(prev => prev.filter(item => item.id !== itemId));
  };

  const updateCustomer = (field: keyof DeliveryCustomer, value: string | boolean) => {
    setCustomer(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (sending.current) return;
    if (!menuCompany) return;
    if (cart.length === 0) {
      alert('Adicione pelo menos um item no carrinho.');
      return;
    }

    if (!customer.name || !customer.phone || (fulfillmentType === 'DELIVERY' && (!customer.address || !customer.number || !customer.neighborhood))) {
      alert(fulfillmentType === 'DELIVERY' ? 'Preencha nome, telefone e endereço completo.' : 'Preencha nome e telefone para retirada.');
      return;
    }
    if (!deliverySettings.isOpen) {
      alert('A loja não está recebendo pedidos agora.');
      return;
    }
    if (regionUnavailable) {
      alert('Este bairro ainda não é atendido.');
      return;
    }
    if (subtotal + 0.001 < deliverySettings.minimumOrder) {
      alert(`Pedido mínimo de ${formatCurrency(deliverySettings.minimumOrder)}.`);
      return;
    }

    localStorage.setItem(CUSTOMER_STORAGE_KEY, JSON.stringify(customer));
    sending.current = true;
    setIsSending(true);
    try {
      const order = await createDeliveryOrder({
        companyId: menuCompany.id,
        companyName: menuCompany.name,
        customer,
        items: cart.map(item => ({
          id: item.id,
          productId: item.product.id,
          productName: item.product.name,
          quantity: item.quantity,
          price: item.product.price,
          notes: item.notes,
          modifierSelections: item.modifierSelections,
        })),
        fulfillmentType,
        paymentMethod,
        changeFor: paymentMethod === 'Dinheiro' && changeFor ? Number(changeFor) : undefined,
        deliveryFee,
        subtotal,
        total,
        notes: orderNotes,
      });

      setCart([]);
      navigate(`/delivery/track/${order.id}?token=${encodeURIComponent(order.publicToken || '')}`);
    } catch (error: any) {
      alert(error.message || 'Pedido não confirmado. Seus itens continuam no carrinho.');
    } finally {
      sending.current = false;
      setIsSending(false);
    }
  };

  if (isLoadingPublicMenu) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8 text-center max-w-md">
          <ShoppingBag className="w-14 h-14 text-slate-300 mx-auto mb-4 animate-pulse" />
          <h1 className="text-2xl font-black text-slate-900">Carregando cardápio</h1>
          <p className="text-sm text-slate-500 mt-2">Buscando produtos liberados para delivery.</p>
        </div>
      </div>
    );
  }

  if (!menuCompany) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8 text-center max-w-md">
          <ShoppingBag className="w-14 h-14 text-slate-300 mx-auto mb-4" />
          <h1 className="text-2xl font-black text-slate-900">Cardápio indisponível</h1>
          <p className="text-sm text-slate-500 mt-2">Abra pelo link público do restaurante ou entre no sistema para testar localmente.</p>
        </div>
      </div>
    );
  }

  if (isDeliveryDisabled) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8 text-center max-w-md">
          <ShoppingBag className="w-14 h-14 text-slate-300 mx-auto mb-4" />
          <h1 className="text-2xl font-black text-slate-900">Delivery indisponível</h1>
          <p className="text-sm text-slate-500 mt-2">Este restaurante não está recebendo pedidos online no momento.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <header className="bg-slate-50 px-4 pt-4">
        <div className="max-w-5xl mx-auto">
          <div className="bg-gradient-to-br from-brand-700 via-slate-900 to-slate-950 rounded-[2rem] overflow-hidden relative shadow-xl p-5 sm:p-8 min-h-[210px]">
            <div className="absolute -right-16 -top-16 w-56 h-56 rounded-full bg-white/10 blur-3xl"></div>
            <div className="absolute right-5 bottom-5 w-40 h-40 rounded-[2rem] bg-white/10 rotate-6"></div>
            <div className="relative flex items-start justify-between gap-4">
              <div className="inline-flex items-center gap-2 bg-white/10 border border-white/10 rounded-full px-3 py-1 text-[10px] font-black uppercase tracking-widest text-white/80">
                <Sparkles className="w-3.5 h-3.5" /> Cardápio FlowTab
              </div>
              <img src="/flowtab-logo-dark-transparent.png" alt="FlowTab" className="w-24 object-contain invert opacity-35" />
            </div>
            <div className="relative mt-8 text-white">
              <h1 className="text-3xl sm:text-5xl font-black tracking-tight">{menuCompany.name}</h1>
              <p className="text-white/70 text-sm sm:text-base mt-2 max-w-xl">Escolha seus itens, informe o endereço e acompanhe o pedido pelo FlowTab.</p>
            </div>
          </div>

          <div className="-mt-6 relative z-10">
            <div className="bg-white rounded-[1.75rem] border border-slate-200 shadow-sm p-4 grid sm:grid-cols-3 gap-3">
              <div className="rounded-2xl bg-emerald-50 border border-emerald-100 p-3 flex items-center gap-3">
                <Bike className="w-8 h-8 text-emerald-600" />
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-emerald-600">Entrega</p>
                  <p className="text-sm font-black text-emerald-900 mt-1">{deliverySettings.estimatedMinutes} min</p>
                </div>
              </div>
              <div className="rounded-2xl bg-brand-50 border border-brand-100 p-3 flex items-center gap-3">
                <CreditCard className="w-8 h-8 text-brand-600" />
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-brand-600">Pagamento</p>
                  <p className="text-sm font-black text-brand-900 mt-1">Pix, cartão ou dinheiro</p>
                </div>
              </div>
              <div className="rounded-2xl bg-slate-50 border border-slate-200 p-3 flex items-center gap-3">
                <MessageCircle className="w-8 h-8 text-slate-500" />
                <div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">Status</p>
                  <p className={`text-sm font-black mt-1 flex items-center gap-1 ${deliverySettings.isOpen ? 'text-slate-900' : 'text-red-600'}`}><Clock className="w-4 h-4" /> {deliverySettings.isOpen ? 'Recebendo pedidos' : 'Loja fechada'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-6 pb-36 space-y-8">
        {!deliverySettings.isOpen && <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-bold text-red-800">A loja está fechada no momento. Você pode consultar o cardápio, mas não enviar pedido.</div>}
        {deliveryProducts.length > 0 && (
          <section className="sticky top-0 z-30 bg-slate-50/95 backdrop-blur py-3 -my-3 space-y-3">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                value={searchTerm}
                onChange={event => setSearchTerm(event.target.value)}
                placeholder="Buscar item, categoria ou bebida..."
                className="w-full h-14 rounded-2xl border border-slate-200 bg-white pl-12 pr-4 text-sm font-bold outline-none shadow-sm focus:border-brand-400"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar">
              {['Todos', ...deliveryCategories].map(category => (
                <button
                  key={category}
                  type="button"
                  onClick={() => setActiveCategory(category)}
                  className={`whitespace-nowrap rounded-full px-4 py-2 text-xs font-black transition-colors ${activeCategory === category ? 'bg-slate-950 text-white shadow-lg' : 'bg-white text-slate-600 border border-slate-200'}`}
                >
                  {category}
                </button>
              ))}
            </div>
          </section>
        )}

        {deliveryProducts.length === 0 && (
          <div className="bg-white border border-dashed border-slate-200 rounded-[2rem] p-8 text-center">
            <ShoppingBag className="w-14 h-14 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-black text-slate-900">Nenhum produto no delivery</h2>
            <p className="text-sm text-slate-500 mt-2">Marque os itens em Cardápio &gt; Editar produto &gt; Vender no Delivery.</p>
          </div>
        )}

        {deliveryProducts.length > 0 && filteredProducts.length === 0 && (
          <div className="bg-white border border-dashed border-slate-200 rounded-[2rem] p-8 text-center">
            <Search className="w-14 h-14 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-black text-slate-900">Nada encontrado</h2>
            <p className="text-sm text-slate-500 mt-2">Tente outro nome ou escolha outra categoria.</p>
          </div>
        )}

        {featuredProducts.length > 0 && (
          <section className="bg-white rounded-[2rem] border border-slate-200 shadow-sm p-4">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-black flex items-center gap-2"><Star className="w-5 h-5 text-amber-400 fill-amber-400" /> Destaques da casa</h2>
              <span className="text-[10px] font-black uppercase tracking-widest text-brand-600 bg-brand-50 rounded-full px-3 py-1 flex items-center gap-1">
                <ShieldCheck className="w-3.5 h-3.5" /> FlowTab
              </span>
            </div>
            <div className="flex gap-4 overflow-x-auto pb-3 no-scrollbar">
              {featuredProducts.map(product => (
                <button key={product.id} type="button" onClick={() => addToCart(product)} className="w-36 flex-shrink-0 text-left group">
                  <div className="h-32 bg-slate-50 rounded-3xl border border-slate-100 p-3 flex items-center justify-center">
                    <img src={product.image} alt={product.name} className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform" />
                  </div>
                  <h3 className="font-black text-sm mt-3 leading-tight line-clamp-2">{product.name}</h3>
                  <p className="text-sm font-black text-slate-700">{formatCurrency(product.price)}</p>
                </button>
              ))}
            </div>
          </section>
        )}

        {productsByCategory.map(group => (
          <section key={group.category} className="space-y-3">
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-black">{group.category}</h2>
              <span className="text-xs font-black text-slate-400">{group.items.length} item(ns)</span>
            </div>
            <div className="space-y-3">
              {group.items.map(product => (
                <article key={product.id} className="bg-white rounded-3xl border border-slate-200 shadow-sm p-3 flex gap-4 items-stretch hover:shadow-md transition-shadow">
                  <button type="button" onClick={() => addToCart(product)} className="flex-1 text-left py-2 pl-1">
                    <h3 className="font-black text-slate-900">{product.name}</h3>
                    {product.description && <p className="text-sm text-slate-500 mt-1 line-clamp-2">{product.description}</p>}
                    <p className="text-base font-black text-slate-900 mt-3">{formatCurrency(product.price)}</p>
                  </button>
                  <div className="w-28 h-28 sm:w-32 sm:h-32 bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-center relative overflow-hidden flex-shrink-0">
                    <img src={product.image} alt={product.name} className="w-full h-full object-contain p-2" />
                    <button type="button" onClick={() => addToCart(product)} className="absolute right-2 bottom-2 w-9 h-9 rounded-full bg-slate-950 text-white flex items-center justify-center shadow-lg">
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </article>
              ))}
            </div>
          </section>
        ))}
      </main>

      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 shadow-2xl p-4 z-40">
        <div className="max-w-5xl mx-auto flex items-center gap-3">
          <div className="flex-1">
            <p className="text-xs font-bold text-slate-500">{cart.length} item(ns) no carrinho</p>
            <p className="text-xl font-black text-slate-900">{formatCurrency(total)}</p>
          </div>
          <Button onClick={() => setIsCheckoutOpen(true)} disabled={cart.length === 0 || !deliverySettings.isOpen} className="rounded-2xl h-14 px-5">
            <ShoppingBag className="w-5 h-5" /> Finalizar
          </Button>
        </div>
      </div>

      {isCheckoutOpen && (
        <div className="fixed inset-0 bg-slate-950/60 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <form onSubmit={handleSubmit} className="bg-white w-full sm:max-w-3xl max-h-[92vh] overflow-y-auto rounded-t-[2rem] sm:rounded-[2rem] shadow-2xl">
            <div className="sticky top-0 bg-white border-b border-slate-100 p-4 flex items-center justify-between z-10">
              <button type="button" onClick={() => setIsCheckoutOpen(false)} className="p-2 rounded-full bg-slate-100"><ChevronLeft className="w-5 h-5" /></button>
              <h2 className="font-black text-lg">Finalizar pedido</h2>
              <div className="w-9" />
            </div>

            <div className="p-4 grid lg:grid-cols-[1fr_340px] gap-5">
              <section className="space-y-4">
                <div className="grid grid-cols-2 gap-2 rounded-3xl bg-slate-100 p-1">
                  <button type="button" onClick={() => setFulfillmentType('DELIVERY')} className={`rounded-2xl px-3 py-3 text-sm font-black ${fulfillmentType === 'DELIVERY' ? 'bg-white text-slate-950 shadow-sm' : 'text-slate-500'}`}>Entrega</button>
                  <button type="button" disabled={!deliverySettings.allowPickup} onClick={() => setFulfillmentType('PICKUP')} className={`rounded-2xl px-3 py-3 text-sm font-black disabled:opacity-40 ${fulfillmentType === 'PICKUP' ? 'bg-white text-slate-950 shadow-sm' : 'text-slate-500'}`}>Retirada no local</button>
                </div>
                <div className="bg-slate-50 rounded-3xl p-4 border border-slate-200">
                  <h3 className="font-black flex items-center gap-2"><UserRound className="w-5 h-5" /> Seus dados</h3>
                  <div className="grid sm:grid-cols-2 gap-3 mt-3">
                    <input required placeholder="Nome completo" value={customer.name} onChange={event => updateCustomer('name', event.target.value)} className="p-3 rounded-2xl border border-slate-200 outline-none text-sm font-bold" />
                    <input required placeholder="Telefone / WhatsApp" value={customer.phone} onChange={event => updateCustomer('phone', event.target.value)} className="p-3 rounded-2xl border border-slate-200 outline-none text-sm font-bold" />
                    {fulfillmentType === 'DELIVERY' && <>
                      <input required placeholder="Rua / Avenida" value={customer.address} onChange={event => updateCustomer('address', event.target.value)} className="sm:col-span-2 p-3 rounded-2xl border border-slate-200 outline-none text-sm font-bold" />
                      <input required placeholder="Número" value={customer.number} onChange={event => updateCustomer('number', event.target.value)} className="p-3 rounded-2xl border border-slate-200 outline-none text-sm font-bold" />
                      <input required placeholder="Bairro" value={customer.neighborhood} onChange={event => updateCustomer('neighborhood', event.target.value)} className="p-3 rounded-2xl border border-slate-200 outline-none text-sm font-bold" />
                      <input placeholder="Complemento" value={customer.complement || ''} onChange={event => updateCustomer('complement', event.target.value)} className="p-3 rounded-2xl border border-slate-200 outline-none text-sm font-bold" />
                      <input placeholder="Ponto de referência" value={customer.reference || ''} onChange={event => updateCustomer('reference', event.target.value)} className="p-3 rounded-2xl border border-slate-200 outline-none text-sm font-bold" />
                    </>}
                  </div>
                  <label className="mt-3 flex items-start gap-3 rounded-2xl bg-emerald-50 border border-emerald-100 p-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={customer.wantsWhatsappUpdates !== false}
                      onChange={event => updateCustomer('wantsWhatsappUpdates', event.target.checked)}
                      className="mt-1"
                    />
                    <span className="text-xs font-bold text-emerald-900">
                      Receber atualizações do pedido pelo WhatsApp quando a automação oficial estiver ativa.
                    </span>
                  </label>
                </div>

                <div className="bg-slate-50 rounded-3xl p-4 border border-slate-200">
                  <h3 className="font-black">Pagamento</h3>
                  <select value={paymentMethod} onChange={event => setPaymentMethod(event.target.value as DeliveryPaymentMethod)} className="w-full mt-3 p-3 rounded-2xl border border-slate-200 outline-none text-sm font-bold bg-white">
                    <option>Pix</option>
                    <option>Dinheiro</option>
                    <option>Cartão Crédito</option>
                    <option>Cartão Débito</option>
                  </select>
                  {paymentMethod === 'Dinheiro' && (
                    <input type="number" step="0.01" placeholder="Troco para quanto?" value={changeFor} onChange={event => setChangeFor(event.target.value)} className="w-full mt-3 p-3 rounded-2xl border border-slate-200 outline-none text-sm font-bold" />
                  )}
                  <textarea placeholder="Observação do pedido" value={orderNotes} onChange={event => setOrderNotes(event.target.value)} className="w-full mt-3 p-3 rounded-2xl border border-slate-200 outline-none text-sm font-bold min-h-[90px]" />
                </div>
              </section>

              <aside className="space-y-3">
                <h3 className="font-black flex items-center gap-2"><Bike className="w-5 h-5" /> Resumo</h3>
                {cart.map(item => (
                  <div key={item.id} className="bg-white border border-slate-200 rounded-2xl p-3">
                    <div className="flex justify-between gap-3">
                      <div>
                        <p className="font-black text-sm">{item.product.name}</p>
                        <p className="text-xs font-bold text-slate-500">{formatCurrency(item.product.price)}</p>
                      </div>
                      <button type="button" onClick={() => removeItem(item.id)} className="text-slate-300 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                      <button type="button" onClick={() => changeQuantity(item.id, -1)} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center"><Minus className="w-4 h-4" /></button>
                      <span className="font-black">{item.quantity}</span>
                      <button type="button" onClick={() => changeQuantity(item.id, 1)} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center"><Plus className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
                <div className="bg-slate-950 text-white rounded-3xl p-4 space-y-2">
                  <div className="flex justify-between text-sm"><span>Subtotal</span><strong>{formatCurrency(subtotal)}</strong></div>
                  <div className="flex justify-between text-sm"><span>{fulfillmentType === 'PICKUP' ? 'Retirada' : 'Taxa entrega'}</span><strong>{deliveryFee === 0 ? 'Grátis' : formatCurrency(deliveryFee)}</strong></div>
                  {deliverySettings.minimumOrder > 0 && <div className="flex justify-between text-xs text-white/70"><span>Pedido mínimo</span><strong>{formatCurrency(deliverySettings.minimumOrder)}</strong></div>}
                  <div className="flex justify-between text-xl font-black pt-2 border-t border-white/10"><span>Total</span><span>{formatCurrency(total)}</span></div>
                </div>
                <Button type="submit" disabled={isSending || !deliverySettings.isOpen || regionUnavailable} className="w-full rounded-2xl h-14">{isSending ? 'Confirmando...' : 'Enviar pedido'}</Button>
              </aside>
            </div>
          </form>
        </div>
      )}
    {customizingProduct && <ProductCustomizationModal product={customizingProduct} onCancel={() => setCustomizingProduct(null)} onConfirm={confirmCustomization} />}
    </div>
  );
};
